import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axios';
import config from '../../config/config';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Modal from '../../components/common/AdminModal';
import Toast from '../../components/common/Toast';
import AdvertiseForm from '../../components/admin/AdvertiseForm';
import { useMainCompany } from '../../context/MainCompanyContext';

export default function AdminAdvertise() {
  const { selectedMain } = useMainCompany();
  const [companies, setCompanies] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterCompany, setFilterCompany] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [busyIds, setBusyIds] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const showToast = (message, type = 'success', duration = 4000) => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  const fetchCompanies = useCallback(async () => {
    if (!selectedMain) return setCompanies([]);
    try {
      // use lightweight dropdown endpoint that returns [{id, company_name}]
      const resp = await api.get(`/admin/sub-companies/dropdown/${selectedMain}`);
      const body = resp.data;
      // backend returns plain array of objects: [{ id, company_name }]
      const items = Array.isArray(body) ? body : [];
      setCompanies(items);
    } catch (e) {
      console.error('Failed loading companies', e?.response?.data || e.message);
      setCompanies([]);
    }
  }, [selectedMain]);

  const fetchAds = useCallback(async (p = page, l = limit) => {
    if (!selectedMain) return;
    setLoading(true);
    try {
      const params = { maincompanyid: selectedMain, page: p, limit: l };
      if (filterCompany) params.companyid = filterCompany;
      const resp = await api.get('/admin/advertise', { params });
      const body = resp.data;
      // shapes may vary; try to normalize
      let items = [];
      let meta = { total: 0, page: p, limit: l };
      if (body && body.data && Array.isArray(body.data)) items = body.data;
      else if (Array.isArray(body)) items = body;
      else if (body && body.data && body.data.data && Array.isArray(body.data.data)) items = body.data.data;
      if (body && body.meta) meta = { ...meta, ...body.meta };
      setAds(items);
      setTotal(meta.total || items.length);
      setPage(meta.page || p);
      setLimit(meta.limit || l);
    } catch (e) {
      console.error('Failed loading ads', e?.response?.data || e.message);
      showToast(e?.response?.data?.message || 'Failed to load advertisements', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedMain, filterCompany, page, limit]);

  useEffect(() => {
    if (selectedMain) {
      fetchCompanies();
      fetchAds(1);
    }
  }, [selectedMain, fetchCompanies, fetchAds]);

  useEffect(() => {
    // when filtering by company, reload page 1
    fetchAds(1);
  }, [filterCompany, fetchAds]);

  // Dev-only logging to help debug image URLs
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && ads && ads.length) {
      console.log('AdminAdvertise debug: selectedMain=', selectedMain, 'firstAd.image=', ads[0].campaign_images);
      const a = ads[0];
      if (a && a.campaign_images) {
        const url = a.campaign_images.startsWith('/uploads') ? `${config.assetsBaseUrl}${a.campaign_images}` : `${config.assetsBaseUrl}/uploads/advertise/${a.campaign_images}`;
        console.log('AdminAdvertise debug: computed image url=', url);
      }
    }
  }, [ads, selectedMain]);

  const onCreated = async () => {
    setShowCreate(false);
    showToast('Created successfully', 'success');
    await fetchAds(1);
  };

  const onUpdated = async () => {
    setEditingAd(null);
    showToast('Saved successfully', 'success');
    await fetchAds(page);
  };

  const toggleStatus = async (ad) => {
    const id = ad.id;
    const newStatus = ad.published ? 0 : 1;
    setBusyIds((s) => ({ ...s, [id]: true }));
    try {
      await api.patch(`/admin/advertise/${id}/status/${newStatus}`);
      showToast('Updated status', 'success');
      await fetchAds(page);
    } catch (e) {
      console.error('Status update failed', e?.response?.data || e.message);
      showToast(e?.response?.data?.message || 'Failed to update status', 'error');
    } finally {
      setBusyIds((s) => { const copy = { ...s }; delete copy[id]; return copy; });
    }
  };

  const onDelete = async (id) => {
    // Open confirmation modal instead of using window.confirm to satisfy lint rules
    setDeleteTargetId(id);
    setShowDeleteConfirm(true);
  };

  const performDelete = async () => {
    const id = deleteTargetId;
    if (!id) return;
    setDeleting(true);
    setBusyIds((s) => ({ ...s, [id]: true }));
    try {
      await api.delete(`/admin/advertise/${id}`);
      showToast('Deleted', 'success');
      await fetchAds(page);
      setShowDeleteConfirm(false);
      setDeleteTargetId(null);
    } catch (e) {
      console.error('Delete failed', e?.response?.data || e.message);
      showToast(e?.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
      setBusyIds((s) => { const copy = { ...s }; delete copy[id]; return copy; });
    }
  };

  return (
    <div className="p-4">
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
      <AdminPageHeader
        title="Advertise Management"
        actions={(
          <div className="flex items-center gap-2">
            <select value={filterCompany} onChange={(e) => setFilterCompany(Number(e.target.value) || 0)} className="border px-2 py-1 rounded bg-white text-black text-sm">
              <option value={0}>All Companies</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.company_name || c.company}</option>)}
            </select>
            <button className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" onClick={() => setShowCreate(true)}>Create</button>
          </div>
        )}
      />

      <div className="overflow-x-auto bg-white rounded border">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-2 py-2 text-left">Campaign Name</th>
              <th className="px-2 py-2 text-left">Campaign URL</th>
              <th className="px-2 py-2 text-left">Image</th>
              <th className="px-2 py-2 text-left">Priority</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Created</th>
              <th className="px-2 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-4 text-center text-gray-500">Loading...</td></tr>
            ) : ads.length > 0 ? (
              ads.map((a) => (
                <tr key={a.id} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-2 align-middle">{a.campaign_name}</td>
                  <td className="px-2 py-2 align-middle break-words max-w-xs"><a href={a.campaign_url} target="_blank" rel="noreferrer" className="text-blue-600">{a.campaign_url}</a></td>
                  <td className="px-2 py-2 align-middle">
                    <img
                      src={
                        a.campaign_images
                          ? (a.campaign_images.startsWith('/uploads') ? `${config.assetsBaseUrl}${a.campaign_images}` : `${config.assetsBaseUrl}/uploads/advertise/${a.campaign_images}`)
                          : `${config.assetsBaseUrl}/uploads/placeholder.png`
                      }
                      alt="thumb"
                      className="w-24 h-auto"
                    />
                  </td>
                  <td className="px-2 py-2 align-middle">{a.priority === 1 ? 'Top' : 'Bottom'}</td>
                  <td className="px-2 py-2 align-middle">{a.published ? 'Active' : 'Inactive'}</td>
                  <td className="px-2 py-2 align-middle">{a.created ? new Date(a.created).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</td>
                  <td className="px-2 py-2 align-middle">
                    <div className="flex gap-2">
                        <button disabled={busyIds[a.id]} onClick={() => toggleStatus(a)} className="px-3 py-1 text-xs bg-gray-200 text-black rounded hover:bg-gray-300">{a.published ? 'Disable' : 'Enable'}</button>
                      <button onClick={() => setEditingAd(a)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded">Edit</button>
                      <button onClick={() => onDelete(a.id)} disabled={busyIds[a.id]} className="px-3 py-1 text-xs bg-red-600 text-white rounded">Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={7} className="py-4 text-center text-gray-500">No advertisements found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {Math.ceil((total || ads.length) / limit) > 1 && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total || ads.length)} of {total || ads.length}</span>
          <div className="flex gap-2 items-center">
            <label>Rows</label>
            <select value={limit} onChange={async (e) => { const l = Number(e.target.value) || 10; setLimit(l); await fetchAds(1,l); }} className="border rounded px-2 py-1">
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <button onClick={async () => { const target = Math.max(1, page - 1); await fetchAds(target); }} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
            <span>{page} / {Math.ceil((total || ads.length) / limit)}</span>
            <button onClick={async () => { const last = Math.ceil((total || ads.length) / limit) || 1; const target = Math.min(last, page + 1); await fetchAds(target); }} disabled={page >= Math.ceil((total || ads.length) / limit)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      )}

      {showCreate && (
        <Modal title="Create Advertisement" onClose={() => setShowCreate(false)}>
          <AdvertiseForm
            mode="create"
            mainCompanyId={selectedMain}
            companies={companies}
            onClose={() => setShowCreate(false)}
            onCreated={onCreated}
          />
        </Modal>
      )}

      {editingAd && (
        <Modal title="Edit Advertisement" onClose={() => setEditingAd(null)}>
          <AdvertiseForm
            mode="edit"
            initial={editingAd}
            mainCompanyId={selectedMain}
            companies={companies}
            onClose={() => setEditingAd(null)}
            onCreated={onUpdated}
          />
        </Modal>
      )}

      {showDeleteConfirm && (
        <Modal title="Delete Advertisement" onClose={() => setShowDeleteConfirm(false)}>
          <div>
            <p>Are you sure you want to permanently delete this advertisement?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={performDelete} disabled={deleting} className="px-3 py-1 bg-red-600 text-white rounded">{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
